import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiGift, FiUsers, FiCreditCard, FiArrowUp, FiArrowDown, FiCopy, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import walletService from '../../services/wallet.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useLanguage } from '../../context/LanguageContext';

const WalletPage = () => {
  const { t } = useLanguage();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [w, txn] = await Promise.all([walletService.get(), walletService.getTransactions({ limit: 50 })]);
        setWallet(w.data?.data);
        setTransactions(txn.data?.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await walletService.applyReferral(referralCode);
      toast.success(data.message);
      setWallet(data.data);
      setReferralCode('');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleTopUp = async () => {
    const amt = parseFloat(topUpAmount);
    if (!amt || amt < 1 || amt > 500) { toast.warning('Enter amount between $1-$500'); return; }
    setSubmitting(true);
    try {
      const { data } = await walletService.topUp(amt);
      toast.success(data.message);
      setWallet(data.data);
      setTopUpAmount('');
      const txn = await walletService.getTransactions({ limit: 50 });
      setTransactions(txn.data?.data || []);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(wallet?.referralCode || '');
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <LoadingSpinner />;

  const txnIcon = (type) => {
    if (['cashback', 'referral_bonus', 'gift_card_redeem', 'refund', 'top_up', 'admin_credit'].includes(type)) return <FiArrowDown className="text-success" />;
    return <FiArrowUp className="text-danger" />;
  };

  return (
    <div className="section-padding" style={{ minHeight: '80vh' }}>
      <div className="container fd-page-container" style={{ maxWidth: '900px' }}>
        <h3 className="fw-bold mb-4"><FiCreditCard className="me-2" />My Wallet</h3>

        {/* Balance Cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <div className="dashboard-card text-center" style={{ background: 'linear-gradient(135deg, #B91C1C, #991B1B)', color: '#FFF', borderRadius: '16px' }}>
              <FiDollarSign size={28} style={{ opacity: 0.7 }} />
              <h2 className="fw-bold mt-2 mb-0">${wallet?.balance?.toFixed(2) || '0.00'}</h2>
              <small style={{ opacity: 0.8 }}>Available Balance</small>
            </div>
          </div>
          <div className="col-md-4">
            <div className="dashboard-card text-center">
              <FiGift size={24} className="text-warning" />
              <h4 className="fw-bold mt-2 mb-0">${wallet?.lifetimeEarnings?.toFixed(2) || '0.00'}</h4>
              <small className="text-muted">Lifetime Earnings</small>
            </div>
          </div>
          <div className="col-md-4">
            <div className="dashboard-card text-center">
              <FiUsers size={24} className="text-primary" />
              <h4 className="fw-bold mt-2 mb-0">{wallet?.referralCount || 0}</h4>
              <small className="text-muted">Referrals</small>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {/* Left: Actions */}
          <div className="col-lg-5">
            {/* Referral Code */}
            <div className="dashboard-card mb-3">
              <h6 className="fw-bold mb-2"><FiUsers className="me-2" />Your Referral Code</h6>
              <div className="d-flex gap-2 align-items-center mb-3">
                <code style={{ fontSize: '18px', fontWeight: 700, color: '#B91C1C', background: '#FEF2F2', padding: '8px 16px', borderRadius: '8px', flex: 1, textAlign: 'center' }}>
                  {wallet?.referralCode || '—'}
                </code>
                <button className="btn btn-sm btn-outline-secondary" onClick={copyCode}>
                  {copied ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
              <small className="text-muted">Share this code — you earn $5, your friend earns $3!</small>
            </div>

            {/* Apply Referral */}
            <div className="dashboard-card mb-3">
              <h6 className="fw-bold mb-2"><FiGift className="me-2" />Have a Referral Code?</h6>
              {wallet?.referredBy ? (
                <p className="text-success small mb-0">✅ Referral code already applied</p>
              ) : (
                <div className="d-flex gap-2">
                  <input className="form-control form-control-sm" placeholder="Enter code"
                    value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} />
                  <button className="btn btn-sm btn-fd-primary" onClick={handleApplyReferral} disabled={submitting}>
                    {submitting ? '...' : 'Apply'}
                  </button>
                </div>
              )}
            </div>

            {/* Top Up */}
            <div className="dashboard-card">
              <h6 className="fw-bold mb-2"><FiDollarSign className="me-2" />Add Funds</h6>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {[5, 10, 25, 50, 100].map(amt => (
                  <button key={amt} className={`btn btn-sm ${parseFloat(topUpAmount) === amt ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => setTopUpAmount(String(amt))}>
                    ${amt}
                  </button>
                ))}
              </div>
              <div className="d-flex gap-2">
                <input type="number" className="form-control form-control-sm" placeholder="Custom amount"
                  value={topUpAmount} onChange={e => setTopUpAmount(e.target.value)} min="1" max="500" />
                <button className="btn btn-sm btn-fd-primary" onClick={handleTopUp} disabled={submitting}>
                  {submitting ? '...' : 'Top Up'}
                </button>
              </div>
              <small className="text-muted mt-1 d-block">Min $1, Max $500</small>
            </div>
          </div>

          {/* Right: Transaction History */}
          <div className="col-lg-7">
            <div className="dashboard-card">
              <h6 className="fw-bold mb-3">Transaction History</h6>
              {transactions.length === 0 ? (
                <p className="text-muted text-center py-3">No transactions yet</p>
              ) : (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {transactions.map((txn, i) => (
                    <div key={i} className="d-flex align-items-center justify-content-between py-2"
                      style={{ borderBottom: i < transactions.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                      <div className="d-flex align-items-center gap-2">
                        {txnIcon(txn.type)}
                        <div>
                          <p className="mb-0 small fw-semibold">{txn.description}</p>
                          <small className="text-muted">{new Date(txn.createdAt).toLocaleDateString()}</small>
                        </div>
                      </div>
                      <span className={`fw-bold ${txn.amount > 0 ? 'text-success' : 'text-danger'}`}>
                        {txn.amount > 0 ? '+' : ''}${Math.abs(txn.amount).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="dashboard-card mt-4">
          <h6 className="fw-bold mb-3">💡 How Wallet Works</h6>
          <div className="row g-3">
            <div className="col-md-3 text-center">
              <div style={{ fontSize: '28px' }}>🛒</div>
              <small className="fw-semibold d-block">Earn Cashback</small>
              <small className="text-muted">2% cashback on orders over $20</small>
            </div>
            <div className="col-md-3 text-center">
              <div style={{ fontSize: '28px' }}>👫</div>
              <small className="fw-semibold d-block">Refer Friends</small>
              <small className="text-muted">Earn $5 per referral</small>
            </div>
            <div className="col-md-3 text-center">
              <div style={{ fontSize: '28px' }}>🎁</div>
              <small className="fw-semibold d-block">Redeem Gift Cards</small>
              <small className="text-muted">Add gift card balance to wallet</small>
            </div>
            <div className="col-md-3 text-center">
              <div style={{ fontSize: '28px' }}>💳</div>
              <small className="fw-semibold d-block">Pay at Checkout</small>
              <small className="text-muted">Use balance for purchases</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletPage;
